import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { uniq } from 'lodash';
import { differenceInDays, parseISO } from 'date-fns';
import { LOGGEDIN_LINKS } from '@web/molecules';
import {
  Hero,
  Footer,
  LoadingView,
  ConfirmationBanner,
  ConfirmationPoll,
  GetSocial,
} from '@web/components';
import { usePets } from '../../pets';
import { useSubscriptions } from '../../subscriptions';
import { useProductsList } from '../../products';
import { useSubscriptionAddonProductsList } from '../../addons';
import { useReporter } from '../../reporter';
import { SlimConfirmationOrderSummary } from '../SlimConfirmationOrderSummary';
import { useOrders, useOrderList } from '../../orders';
import { useSessionCustomer } from '../../customer';
import { AccountHeader } from '../../account/AccountHeader';
import { FOOTER_CONTENT } from '../../testing/constants';
import { RedirectToDashboard } from '../routing';
import { OnboardingContext } from '../OnboardingFlowContainer/context';
import {
  calculateGrandTotals,
  formatDate,
  formatOrderSummary,
  getWebConfirmationOrderSummaryProps,
} from './dataParsers';
import {
  userViewsCheckoutConfirmation,
  userClickedViewAccount,
} from './events';
import {
  StyledCheckoutConfirmationContainer,
  Container,
  LeftSideWrapper,
  RightSideWrapper,
  MobileGetSocialWrapper,
  DesktopGetSocialWrapper,
  HeroContainer,
} from './styles';

export const CheckoutConfirmationContainer = ({
  hero,
  leftSideComponents,
  rightSideComponents,
  orderSummaryTitle,
}) => {
  const { offer } = useContext(OnboardingContext);
  const pricePeriod = 'week';
  const { customer, isLoading: isCustomerLoading } = useSessionCustomer();
  const { subscriptions, isLoading: isSubscriptionsLoading } =
    useSubscriptions();
  const { pets, isLoading: isPetsLoading } = usePets({ hasSubscription: true });
  const { products, isLoading: isProductsLoading } = useProductsList(
    !isSubscriptionsLoading
      ? uniq(subscriptions.map(subscription => subscription.sku))
      : null,
  );
  const { data: addons, isLoading: isAddonsLoading } =
    useSubscriptionAddonProductsList();
  const { orders, isLoading: isOrdersLoading } = useOrders();
  const orderIds = orders?.map(order => order.id);
  const { orders: orderList, isLoading: isOrderListLoading } =
    useOrderList(orderIds);
  const isLoading =
    isSubscriptionsLoading ||
    isPetsLoading ||
    isProductsLoading ||
    isAddonsLoading ||
    isOrdersLoading ||
    isOrderListLoading ||
    isCustomerLoading;
  const reporter = useReporter();
  useEffect(() => {
    if (!isLoading) {
      reporter.tag(userViewsCheckoutConfirmation());
    }
  }, [reporter, isLoading]);

  if (isLoading) {
    return <LoadingView />;
  }

  const firstOrderDate = () => {
    return (
      orders?.reduce((oldest, order) => {
        return oldest < order.date_created ? oldest : order.date_created;
      }, orders[0].date_created) ?? null
    );
  };

  // Redirect to dashboard if order is >= 1 day ago
  if (
    !isLoading &&
    differenceInDays(new Date(), parseISO(firstOrderDate())) >= 1
  ) {
    return (
      <RedirectToDashboard pet={pets[0]}>
        <LoadingView />
      </RedirectToDashboard>
    );
  }

  const ordersSummary = formatOrderSummary(
    pets,
    subscriptions,
    orderList,
    products,
    addons,
    pricePeriod,
  );

  const petNames = [];
  for (let count = 0; count < pets.length; count++) {
    petNames.push(pets[count]?.name);
  }

  const getListOfNames = () => {
    let listOfNames = '';

    const lastPetName = petNames.pop();

    if (!petNames?.length) {
      listOfNames = `${lastPetName}'s`;
    } else {
      listOfNames = `${petNames.join(', ')}, and ${lastPetName}'s`;
    }

    return listOfNames;
  };

  // TODO: Get this from the backend (both price total and after discount)
  const { subtotal, totalTax, totalPrice } = calculateGrandTotals(orderList);
  const deliveryDate = formatDate(orderList[0].delivery_date);
  const confirmationHeroProps = {
    headline: hero.headline,
    subheadline: `${getListOfNames()} starter box is set to arrive on ${deliveryDate}`,
    cta: hero.cta,
    ctaColor: hero.ctaColor,
    image: hero.image,
    backgroundColor: hero.backgroundColor,
    fontColor: hero.fontColor,
  };

  const { formattedPricingList, plans } = getWebConfirmationOrderSummaryProps(
    offer,
    orderList,
    pets,
    products,
  );

  const confirmationOrderSummaryProps = {
    orders: ordersSummary,
    orderTotalCost: totalPrice,
    orderTotalTax: totalTax,
    orderSubtotal: subtotal,
    orderSummaryTitle,
    period: pricePeriod,
    pricingList: formattedPricingList,
    plans: plans,
  };
  const freeItems = rightSideComponents?.find(
    item => item.component === 'free_items',
  );

  return (
    <StyledCheckoutConfirmationContainer>
      <AccountHeader
        links={LOGGEDIN_LINKS}
        logoUrl={'/'}
        logoSrText={'Ollie Logo'}
        name={customer.first_name}
      />
      <HeroContainer>
        <Hero {...confirmationHeroProps} />
      </HeroContainer>
      <Container>
        <LeftSideWrapper>
          {leftSideComponents?.map((item, key) =>
            item.component === 'confirmation_banner' ? (
              <ConfirmationBanner key={key} {...item} />
            ) : (
              item.component === 'confirmation_poll' && (
                <ConfirmationPoll key={key} {...item} />
              )
            ),
          )}
        </LeftSideWrapper>
        {rightSideComponents ? (
          <RightSideWrapper>
            <SlimConfirmationOrderSummary
              {...confirmationOrderSummaryProps}
              freeItems={freeItems.items}
              onClickAccount={() => reporter.tag(userClickedViewAccount())}
            />
            <DesktopGetSocialWrapper>
              {rightSideComponents.map(
                (item, key) =>
                  item.component === 'get_social_component' && (
                    <GetSocial key={key} {...item} />
                  ),
              )}
            </DesktopGetSocialWrapper>
          </RightSideWrapper>
        ) : (
          <SlimConfirmationOrderSummary
            {...confirmationOrderSummaryProps}
            onClickAccount={() => reporter.tag(userClickedViewAccount())}
          />
        )}
        {rightSideComponents && (
          <MobileGetSocialWrapper>
            {rightSideComponents.map(
              (item, key) =>
                item.component === 'get_social_component' && (
                  <GetSocial key={key} {...item} />
                ),
            )}
          </MobileGetSocialWrapper>
        )}
      </Container>
      <Footer {...FOOTER_CONTENT} />
    </StyledCheckoutConfirmationContainer>
  );
};

CheckoutConfirmationContainer.displayName = 'CheckoutConfirmation';
CheckoutConfirmationContainer.propTypes = {
  headline: PropTypes.string.isRequired,
  orderSummaryTitle: PropTypes.string.isRequired,
  leftSideComponents: PropTypes.array.isRequired,
  rightSideComponents: PropTypes.array.isRequired,
};
